import type { UseQueryOptions } from '@tanstack/vue-query'
import { useQuery } from '@tanstack/vue-query'
import type { SearchApiParams, SearchApiResult } from './search-api.types'

type SearchQueryOptions<R = SearchApiResult> = Partial<
  Omit<UseQueryOptions<R>, 'select'>
> & {
  enabled?: MaybeRefOrGetter<boolean | undefined>
  select?(result: SearchApiResult, params: SearchApiParams): R
}

export function useRedesignSearchQuery<R = SearchApiResult>(
  query: MaybeRefOrGetter<SearchApiParams>,
  options: SearchQueryOptions<R> = {},
) {
  const fetch = useRequestFetch()
  const l10n = useL10N()
  const params = computed(() =>
    sortKeys({ language: l10n.language, ...removeEmpty(toValue(query)) }),
  )

  return useQuery({
    ...(options as Omit<typeof options, 'enabled' | 'select'>),
    placeholderData: (prev: SearchApiResult | undefined) => prev, // Keep previous data between requests
    enabled: computed(
      () => toValue(options.enabled) !== false && import.meta.client,
    ),
    queryKey: ['search-api', 'accomtrips', params] as const,
    queryFn({ queryKey: [_, __, q], signal }) {
      return fetch<SearchApiResult>('/search-api/v1/engines/accomtrips', {
        query: mapSearchApiParams(q),
        signal,
      })
    },
    select(result) {
      // TODO: Remove as soon as the backend starts returning already normalized facets.
      // Since technically only the backend knows about the facet aggregation rules.
      const normalizedResult = { ...result }
      if (normalizedResult.facets) {
        normalizedResult.facets = normalizeFacets(normalizedResult.facets)
      }
      return (
        options.select
          ? options.select(normalizedResult, toValue(query))
          : normalizedResult
      ) as R
    },
  })
}

export function useRedesignSearchAccommodationsQuery(
  query: MaybeRefOrGetter<SearchApiParams>,
  options: Omit<SearchQueryOptions, 'select'> = {},
) {
  return useRedesignSearchQuery(query, {
    ...options,
    select(result) {
      return setDocPosition(result, toValue(query))
    },
  })
}

function cumulative(reverse = false) {
  return (facet: SearchApiCountFacet) => getCumulativeFacet(facet, { reverse })
}

const FACETS_NORMALIZATION_MAP: {
  [K in keyof Required<SearchApiFacets>]?: (
    facet: Required<SearchApiFacets>[K],
  ) => SearchApiFacets[K]
} = {
  sea: cumulative(),
  lake: cumulative(),
  ski: cumulative(),
  center: cumulative(),
}

export function normalizeFacets(facets: SearchApiFacets): SearchApiFacets {
  return Object.fromEntries(
    Object.entries(facets).map(([key, facet]) => [
      key,
      key in FACETS_NORMALIZATION_MAP
        ? FACETS_NORMALIZATION_MAP[key as keyof SearchApiFacets]!(facet)
        : facet,
    ]),
  ) as SearchApiFacets
}
